import { Delete01Icon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { SectionCard } from "shared/app/LeadsTableView/components/CreateLeadModal/SectionCard";
import { useFormController } from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  InputSize
} from "shared/components";

export const ContactInfoSection = ({
  form
}: {
  form: ReturnType<typeof useFormController>;
}) => {
  const prevPhoneFieldsNotReady =
    Boolean(form.errors.primary_phone) || Boolean(form.errors.other_phones);

  const prevEmailFieldsNotReady =
    Boolean(form.errors.primary_email) || Boolean(form.errors.other_emails);

  return (
    <SectionCard title="Contact Information" wrapperClassName="!p-0">
      <div className="flex w-full">
        {/* Phone Numbers Section */}
        <div className="flex-1 flex flex-col gap-4 pb-8 pt-6 px-8 border-r border-alpha-40">
          <div className="font-medium text-sm leading-5 text-content-700">
            Phone numbers
          </div>
          <div className="flex flex-col gap-4">
            <div>
              <Input
                id="primaryPhone"
                value={form.values.primary_phone}
                onChange={(e) => form.updatePrimaryPhone(e.target.value)}
                size={InputSize.SM}
                error={form.errors.primary_phone}
                placeholder="(123) 456-7890"
                type="tel"
                legacyErrorMargins={false}
              />
            </div>

            {form.values.other_phones.map((phoneNumber, index) => {
              return (
                <div key={index} className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      id={`phone-${index}`}
                      value={phoneNumber}
                      onChange={(e) =>
                        form.updateOtherPhone(index, e.target.value)
                      }
                      error={form.errors.other_phones?.[index]}
                      size={InputSize.SM}
                      placeholder="(123) 456-7890"
                      type="tel"
                      legacyErrorMargins={false}
                    />
                  </div>
                  <IconButton
                    variant={IconButtonVariant.BASE}
                    size={IconButtonSize.SM}
                    HugeIcon={Delete01Icon}
                    onClick={() => form.removeOtherPhone(index)}
                  />
                </div>
              );
            })}
          </div>
          <div className="border-t border-alpha-40 pt-4">
            <Button
              tooltipZIndex={50}
              tooltipDelay={400}
              tooltip={
                prevPhoneFieldsNotReady
                  ? "Please fix errors in phone fields before adding a new one"
                  : undefined
              }
              disabled={prevPhoneFieldsNotReady}
              size={ButtonSize.SM}
              HugeIcon={PlusSignIcon}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={async () => {
                const errors = await form.triggerPartialValidation([
                  "primary_phone",
                  "other_phones"
                ]);

                if (Object.keys(errors).length) return;

                form.addOtherPhone();
              }}
            >
              Add phone number
            </Button>
          </div>
        </div>

        {/* Emails Section */}
        <div className="flex-1 flex flex-col gap-4 pb-8 pt-6 px-8">
          <div className="font-medium text-sm leading-5 text-content-700">
            Emails
          </div>
          <div className="flex flex-col gap-4">
            <div>
              <Input
                id="primaryEmail"
                value={form.values.primary_email}
                onChange={(e) => form.updatePrimaryEmail(e.target.value)}
                error={form.errors.primary_email}
                size={InputSize.SM}
                placeholder="john.doe@acme.com"
                type="email"
                legacyErrorMargins={false}
              />
            </div>

            {form.values.other_emails.map((otherEmail, localIndex) => {
              return (
                <div
                  key={`email-${localIndex}-${otherEmail}`}
                  className="flex gap-2"
                >
                  <div className="flex-1">
                    <Input
                      id={`email-${localIndex}`}
                      value={otherEmail}
                      onChange={(e) =>
                        form.updateOtherEmail(localIndex, e.target.value)
                      }
                      error={form.errors.other_emails?.[localIndex]}
                      size={InputSize.SM}
                      placeholder="john.doe@acme.com"
                      type="email"
                      legacyErrorMargins={false}
                    />
                  </div>
                  <IconButton
                    variant={IconButtonVariant.BASE}
                    size={IconButtonSize.SM}
                    HugeIcon={Delete01Icon}
                    onClick={() => form.removeOtherEmail(localIndex)}
                  />
                </div>
              );
            })}
          </div>
          <div className="border-t border-alpha-40 pt-4">
            <Button
              tooltipZIndex={50}
              tooltipDelay={300}
              tooltip={
                prevEmailFieldsNotReady
                  ? "Please fix errors in email fields before adding a new one"
                  : undefined
              }
              disabled={prevEmailFieldsNotReady}
              size={ButtonSize.SM}
              HugeIcon={PlusSignIcon}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={async () => {
                const errors = await form.triggerPartialValidation([
                  "primary_email",
                  "other_emails"
                ]);

                if (Object.keys(errors).length) return;

                form.addOtherEmail();
              }}
            >
              Add email
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
